/* eslint-disable react-hooks/exhaustive-deps */
import { type ReactElement, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { norm, trimSlash } from '@/utils';
import { useForm } from 'react-hook-form';
import classNames from 'classnames';
import CTNIcon from '@ctn/ui/components/ctn-icon/icon';
import { useStrapiGlobal } from '@/context';
import type { NavItem } from '@/interfaces/strapi';
import { MenuTree, type SidebarItem } from '@/components';
import { useActiveSection } from '@/hooks';
import CTNButton from '@ctn/ui/components/ctn-button';
import CTNInput from '@ctn/ui/components/ctn-input';

type HeaderProps = {
	readonly className?: string;
	readonly sidebarItems?: SidebarItem[];
};

type SearchForm = {
	search: string;
};

const classNameComponent = 'c-cnm-header-inner';

export const HeaderInner = ({ className, sidebarItems }: HeaderProps): ReactElement => {
	const wrapperClassName = useMemo(() => classNames(classNameComponent, className), [classNameComponent, className]);
	const { pathname } = useLocation();
	const { layout, menu } = useStrapiGlobal();
	const { header } = layout;
	const { logo } = header;
	const form = useForm<SearchForm>({
		defaultValues: { search: '' },
		mode: 'onSubmit',
	});
	const { control } = form;
	const [isMobileOpen, setIsMobileOpen] = useState(false);
	const navItems: NavItem[] = menu.navItems ?? [];
	const activeSection = useActiveSection();
	const currentSectionTitle = useMemo(() => {
		const current = trimSlash(norm(pathname));
		const items = sidebarItems ?? [];

		const normalize = (p?: string) => (p ? trimSlash(norm(p)) : '');

		for (const top of items) {
			const topPath = normalize(top.to);
			const topMatches = topPath && (current === topPath || current.startsWith(topPath + '/'));
			const children = top.children ?? [];

			// ¿algún hijo (o nieto) coincide?
			const childMatches = children.some((ch) => {
				const chPath = normalize(ch.to);
				const m1 = chPath && (current === chPath || current.startsWith(chPath + '/'));
				const gkids = ch.children ?? [];
				const m2 = gkids.some((g) => {
					const gp = normalize(g.to);
					return gp && (current === gp || current.startsWith(gp + '/'));
				});
				return m1 || m2;
			});

			if (topMatches || childMatches) return top.label;
		}

		return 'Inicio';
	}, [pathname, sidebarItems]);

	useEffect(() => {
		const onKeydown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setIsMobileOpen(false);
		};
		document.addEventListener('keydown', onKeydown);

		document.body.classList.toggle('no-scroll', isMobileOpen);

		return () => {
			document.removeEventListener('keydown', onKeydown);
			document.body.classList.remove('no-scroll');
		};
	}, [isMobileOpen]);

	return (
		<header className={wrapperClassName}>
			{/* ===== TOPBAR (MÓVIL) ===== */}
			<div className={`${wrapperClassName}__row ${classNameComponent}__topbar`}>
				<div className={`${classNameComponent}__container`}>
					<span className={`${classNameComponent}__title`}>COLEGIO NOTARIAL DE MADRID</span>

					<div className={`${classNameComponent}__right`}>
						{/* Botón buscar (MÓVIL) */}
						<CTNIcon
							className={`${classNameComponent}__iconbtn ${classNameComponent}__iconbtn--search`}
							icon="search"
						/>

						{/* Form buscador (DESKTOP) */}
						<form className={`${classNameComponent}__form`} role="search">
							<CTNInput
								classNameInput={`${classNameComponent}__input`}
								classNameField={`${classNameComponent}__field`}
								type="text"
								name="search"
								placeholder="Buscar"
								control={control}
								iconRight={{
									icon: 'search',
									color: 'blue-80',
								}}
							/>
						</form>

						<CTNButton
							type="button"
							variant="solid"
							mode="primary"
							className={`${classNameComponent}__btn`}
							icon={[{ icon: 'login', position: 'left', color: 'primary-white' }]}
							label="Acceder"
						/>
					</div>
				</div>
			</div>

			{/* ===== MENUBAR (MÓVIL) ===== */}
			<div
				className={`${classNameComponent}__row ${classNameComponent}__menubar ${isMobileOpen ? 'is-open' : ''}`}
			>
				<div className={`${classNameComponent}__container`}>
					<span className={`${classNameComponent}__pageTitle`} title={currentSectionTitle}>
						{currentSectionTitle}
					</span>

					<CTNIcon
						icon={isMobileOpen ? 'close' : 'hamburger'}
						className={`${classNameComponent}__burger`}
						aria-expanded={isMobileOpen}
						aria-controls={`${classNameComponent}__mobilemenu`}
						onClick={() => setIsMobileOpen((v) => !v)}
					/>
				</div>
			</div>

			{/* ===== BLOQUE DESKTOP (mantiene tu diseño actual) ===== */}
			<div className={`${classNameComponent}__desktopbar`}>
				<div className="o-container">
					<div className={`${classNameComponent}__row`}>
						<div className={`${classNameComponent}__content`}>
							<Link to={logo?.link ?? '/'} className={`${classNameComponent}__brand`} aria-label="Inicio">
								<span className={`${classNameComponent}__brand-top`}>COLEGIO NOTARIAL</span>
								<span className={`${classNameComponent}__brand-bottom`}>DE MADRID</span>
							</Link>

							<MenuTree
								items={navItems}
								variant="desktop"
								classNameBase={classNameComponent}
								activeOverride={activeSection}
							/>
						</div>

						<div className={`${classNameComponent}__actions`}>
							<div className={`${classNameComponent}__iconbtn`}>
								<CTNIcon className={`${classNameComponent}__iconbtn--search`} icon="search" />
							</div>
							<div className={`${classNameComponent}__iconbtn`}>
								<CTNIcon className={`${classNameComponent}__iconbtn--search`} icon="login" />
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* ===== Drawer + Backdrop (móvil/tablet) ===== */}
			{isMobileOpen && (
				<div
					className={`${classNameComponent}__backdrop`}
					aria-hidden="true"
					onClick={() => setIsMobileOpen(false)}
				/>
			)}

			<div
				id={`${classNameComponent}__mobilemenu`}
				className={`${classNameComponent}__mobilemenu ${isMobileOpen ? 'is-open' : ''}`}
				role="region"
				aria-label="Menú móvil"
			>
				<MenuTree
					items={navItems}
					variant="mobile"
					classNameBase={classNameComponent}
					onNavigate={() => setIsMobileOpen(false)}
				/>
			</div>
		</header>
	);
};
